import { observer } from 'mobx-react';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import AgentUserMessage from './messages/AgentUserMessage';
import AgentAIMessage from './messages/AgentAIMessage';
import AgentMessageLoading from './messages/AgentMessageLoading';
import { phaseRendersLoader } from '../utils/MessagePhaseManager';
import { SEARCH_INITIATION_MARKER } from '../adapters/services/SearchWorkflowService';

// TD-2346: format a message timestamp as HH:MM for the "Rerun of …" subtitle
const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// TD-2346: provenance subtitle for a rerun turn ("Rerun of message sent HH:MM"),
// or null when the message isn't a rerun / its source can't be resolved.
const buildRerunSubtitle = (message, messagesById) => {
  if (!message.sourceMessageId) return null;
  const sourceMessage = messagesById.get(message.sourceMessageId);
  if (!sourceMessage) return null;
  const time = formatMessageTime(sourceMessage.timestamp);
  return time ? `Rerun of message sent ${time}` : 'Rerun of an earlier message';
};

/**
 * ✅ ENHANCED: AgentChatMessageList with built-in virtualization
 *
 * Handles large message lists efficiently to solve the memory management crisis.
 * Uses custom virtualization when message count exceeds threshold.
 */
const AgentChatMessageList = observer(
  ({
    agentName = 'Agentic AI',
    isLoading: propIsLoading = false,
    messages = [],
    connectionStatus = 'connected',
    agentState = 'idle',
    searchMetadata = null, // NEW: Search state metadata
    error = null,
    onRetry = null, // NEW: Callback for retrying failed messages
    onRerun = null, // TD-2346: Callback to rerun a previous user message
    rerunDisabled = false, // TD-2346: Disable rerun while a send is not allowed
    onControlReply = null, // TD-2293: hidden enrichment control reply (reveal confirm/cancel)
    onRevealComplete = null, // TD-2293: reveal job settled live → reconcile credit chip
    creditLimit = null // TD-2884: total credit allowance for the confirm card
  }) => {
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState(600);
    const [scrollPosition, setScrollPosition] = useState(0);

    // ✅ FIX: Handle empty or undefined messages gracefully
    const currentMessages = Array.isArray(messages) ? messages : [];
    const isLoading = propIsLoading;

    // Debug logging for development only
    useEffect(() => {
      if (import.meta.env.DEV) {
        console.log(
          '🔍 DEBUG MESSAGE LIST: Component received',
          currentMessages.length,
          'messages'
        );
      }
    }, [currentMessages.length]);

    // ✅ PERFORMANCE: Virtualization threshold
    const VIRTUALIZATION_THRESHOLD = 50; // Start virtualizing at 50+ messages
    const MESSAGE_HEIGHT = 120; // Average message height in pixels
    const BUFFER_SIZE = 5; // Render extra messages above/below viewport

    const useVirtualization = currentMessages.length > VIRTUALIZATION_THRESHOLD;

    // Calculate visible message range for virtualization
    const getVisibleRange = useCallback(() => {
      if (!useVirtualization) {
        return { start: 0, end: currentMessages.length };
      }

      const startIndex = Math.floor(scrollPosition / MESSAGE_HEIGHT);
      const endIndex = Math.min(
        currentMessages.length,
        startIndex + Math.ceil(containerHeight / MESSAGE_HEIGHT) + BUFFER_SIZE
      );

      return {
        start: Math.max(0, startIndex - BUFFER_SIZE),
        end: endIndex
      };
    }, [scrollPosition, containerHeight, useVirtualization, currentMessages.length]);

    const visibleRange = getVisibleRange();

    // Handle scroll for virtualization
    const handleScroll = useCallback(
      (e) => {
        if (useVirtualization) {
          setScrollPosition(e.target.scrollTop);
        }
      },
      [useVirtualization]
    );

    // Measure container height
    useEffect(() => {
      const updateHeight = () => {
        if (containerRef.current) {
          setContainerHeight(containerRef.current.clientHeight);
        }
      };

      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }, []);

    // Auto-scroll to bottom when new messages arrive (but not when user scrolled up)
    const [userScrolledUp, setUserScrolledUp] = useState(false);
    const lastMessageRef = useRef(null);

    const scrollToBottom = useCallback(() => {
      if (!userScrolledUp) {
        // Smart scroll: Try to show the top of the last message with some context
        if (lastMessageRef.current) {
          const container = lastMessageRef.current.closest('.agent-chat-message-list');
          if (container) {
            const containerHeight = container.clientHeight;
            const messageElement = lastMessageRef.current;
            const messageHeight = messageElement.offsetHeight;

            // If message is short enough to fit with context, scroll to bottom
            if (messageHeight < containerHeight * 0.7) {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            } else {
              // For long messages, scroll to show the top with 20% padding from top
              messageElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
              });

              // Add a small offset to show some context from previous message
              setTimeout(() => {
                if (container.scrollTop > 60) {
                  container.scrollTop -= 60;
                }
              }, 100);
            }
          }
        } else {
          // Fallback to original behavior
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, [userScrolledUp]);

    // Detect if user has scrolled up
    const handleUserScroll = useCallback(
      (e) => {
        handleScroll(e); // Handle virtualization scroll

        const container = e.target;
        const isAtBottom =
          container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
        setUserScrolledUp(!isAtBottom);
      },
      [handleScroll]
    );

    useEffect(() => {
      scrollToBottom();
    }, [currentMessages.length, scrollToBottom]);

    // Additional auto-scroll for search progress updates
    useEffect(() => {
      // Scroll to bottom when search state changes or search metadata updates
      if (agentState === 'searching' || searchMetadata) {
        scrollToBottom();
      }
    }, [agentState, searchMetadata, scrollToBottom]);

    // Frequent auto-scroll during search mode to handle growing content
    useEffect(() => {
      if (agentState === 'searching') {
        const scrollInterval = setInterval(() => {
          if (!userScrolledUp) {
            scrollToBottom();
          }
        }, 500); // Every 500ms during search

        return () => clearInterval(scrollInterval);
      }
    }, [agentState, userScrolledUp, scrollToBottom]);

    // ✅ ENHANCED: Render messages with virtualization support
    // TD-2346: index messages by id once (memoized) so the rerun-subtitle source
    // lookup is O(1) per message instead of O(n) — avoids O(n^2) on long threads.
    const messagesById = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

    // Render a single message by type. Extracted from renderMessages so the
    // per-type branching doesn't inflate renderMessages' cognitive complexity.
    const renderMessageItem = (message, actualIndex, isLastMessage) => {
      const itemRef = isLastMessage ? lastMessageRef : null;
      if (message.type === 'user') {
        const subtitle = buildRerunSubtitle(message, messagesById);
        return (
          <div key={message.id || `user-${actualIndex}`} ref={itemRef}>
            <AgentUserMessage
              content={message.content}
              subtitle={subtitle}
              disabled={rerunDisabled}
              onRerun={onRerun ? () => onRerun(message) : null}
            />
          </div>
        );
      }
      if (message.type === 'ai' || message.type === 'agent') {
        return (
          <div key={message.id || `ai-${actualIndex}`} ref={itemRef}>
            <AgentAIMessage
              content={message.content}
              contentType={message.contentType || 'text'}
              tableData={message.tableData}
              agentName={agentName}
              // Pass full message object for phase support
              message={message}
              onRetry={onRetry}
              onControlReply={onControlReply}
              onRevealComplete={onRevealComplete}
              creditLimit={creditLimit}
              // Claude-desktop UX: let AgentAIMessage handle the thinking phase
              showClaudeDesktopStyle={true}
            />
          </div>
        );
      }
      if (message.type === 'system') {
        return (
          <div key={message.id || `system-${actualIndex}`} ref={itemRef}>
            <AgentAIMessage content={message.content} contentType="text" agentName="System" />
          </div>
        );
      }
      if (import.meta.env.DEV) {
        console.warn('Unknown message type:', message.type, 'for message:', message.id);
      }
      return null;
    };

    const renderMessages = () => {
      // Filter out hidden messages (e.g., internal search polling responses) and the internal
      // SEARCH_INITIATION placeholder, which must never render even if its hidden flag hasn't
      // been applied yet (defensive net for the streaming race; primary fix is in onText).
      const visibleMessages = currentMessages.filter(
        (msg) => !msg.hidden && msg.content !== SEARCH_INITIATION_MARKER
      );

      const messagesToRender = useVirtualization
        ? visibleMessages.slice(visibleRange.start, visibleRange.end)
        : visibleMessages;

      // Debug logging for development only
      if (import.meta.env.DEV && messagesToRender.length !== visibleMessages.length) {
        console.log(
          '🔍 DEBUG MESSAGE LIST: Virtualizing',
          messagesToRender.length,
          'of',
          visibleMessages.length,
          'messages'
        );
      }

      const topSpacer = useVirtualization ? visibleRange.start * MESSAGE_HEIGHT : 0;
      const bottomSpacer = useVirtualization
        ? (visibleMessages.length - visibleRange.end) * MESSAGE_HEIGHT
        : 0;

      return (
        <>
          {/* Virtual spacer for items above viewport */}
          {topSpacer > 0 && <div style={{ height: topSpacer }} />}

          {messagesToRender.map((message, index) => {
            const actualIndex = useVirtualization ? visibleRange.start + index : index;
            const isLastMessage = actualIndex === visibleMessages.length - 1;
            return renderMessageItem(message, actualIndex, isLastMessage);
          })}

          {/* Virtual spacer for items below viewport */}
          {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} />}
        </>
      );
    };

    // ✅ ENHANCED: Show agent state and error indicators
    const renderStatusIndicators = () => {
      if (error) {
        return (
          <div className="agent-message-error">
            <div className="agent-error-content">❌ {error}</div>
          </div>
        );
      }

      // ✨ CLAUDE DESKTOP UX: Don't show separate loading indicator if thinking messages exist
      // Thinking messages now render their own loading indicators
      // TD-2341: the loader / search strip belongs to the CURRENT turn — the last visible
      // message. If that message already renders its own loader (it is thinking, or it is a tool
      // running its one-liner via AgentAIMessage), don't render the list-level loader beneath it.
      // Scoped to the last visible message rather than a history-wide scan, so a stale in-progress
      // message from an earlier interrupted turn can't suppress the loader for every later turn.
      const visibleMessages = currentMessages.filter((msg) => !msg.hidden);
      const lastVisible = visibleMessages.at(-1);
      const lastMessageSelfRendersLoader =
        !!lastVisible &&
        (lastVisible.type === 'ai' || lastVisible.type === 'agent') &&
        phaseRendersLoader(lastVisible.phase);

      if (
        (agentState === 'thinking' ||
          agentState === 'responding' ||
          agentState === 'searching' ||
          isLoading) &&
        !lastMessageSelfRendersLoader
      ) {
        const loadingMode = agentState === 'searching' ? 'searching' : 'default';
        if (import.meta.env.DEV) {
          console.log('🔍 AgentChatMessageList: Rendering loading indicator with:', {
            agentState,
            loadingMode,
            searchMetadata,
            searchType: searchMetadata?.toolName,
            searchStartTime: searchMetadata?.startTime
          });
        }

        return (
          <AgentMessageLoading
            mode={loadingMode}
            searchType={searchMetadata?.toolName}
            searchStartTime={searchMetadata?.startTime}
          />
        );
      }

      return null;
    };

    // Show progress while the conversation is actually being loaded.
    // TD-2571: there is no persistent connection here — connect() is a one-shot history fetch —
    // so there is nothing to reconnect to and the old "attempting to reconnect..." banner promised
    // a retry that no code anywhere performed. 'disconnected' is simply the pre-load state and
    // renders nothing; the terminal outcomes (thread unavailable, or load failed with a retry)
    // are owned by AgentChatPage via GeneralError.
    const renderConnectionStatus = () => {
      if (connectionStatus === 'connecting') {
        return <div className="agent-connection-status connecting">🔄 Connecting...</div>;
      }

      return null;
    };

    return (
      <div className="agent-chat-message-list">
        {renderConnectionStatus()}

        <div
          ref={containerRef}
          className={`agent-messages-container ${useVirtualization ? 'virtualized' : ''}`}
          onScroll={handleUserScroll}
          style={{
            height: useVirtualization ? '100%' : 'auto',
            overflowY: useVirtualization ? 'auto' : 'visible'
          }}
        >
          {currentMessages.length > 0 ? (
            renderMessages()
          ) : isLoading ? (
            // Show simple loading text when loading messages from backend
            <div className="agent-messages-empty">
              <div className="agent-empty-content">Loading conversation...</div>
            </div>
          ) : (
            // Only show empty state when NOT loading and no messages
            <div className="agent-messages-empty">
              <div className="agent-empty-content">💬 Start a conversation with the agent</div>
            </div>
          )}

          {renderStatusIndicators()}

          {/* Performance indicator for development */}
          {useVirtualization && (
            <div className="agent-virtualization-indicator">
              📊 Virtualized: showing {visibleRange.end - visibleRange.start} of{' '}
              {currentMessages.length} messages
            </div>
          )}

          {/* Invisible div to ensure auto-scroll works */}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button when user scrolled up */}
        {userScrolledUp && currentMessages.length > 5 && (
          <button
            className="agent-scroll-to-bottom"
            onClick={() => {
              setUserScrolledUp(false);
              scrollToBottom();
            }}
            aria-label="Scroll to bottom"
          >
            ⬇️ New messages
          </button>
        )}
      </div>
    );
  }
);

export default AgentChatMessageList;
